import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Building, Loader2, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ReviewReplySection({ 
  reviewId, 
  businessId, 
  canReply = false,
  existingReply = null 
}) {
  const [reply, setReply] = useState(existingReply);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(!existingReply);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!existingReply) {
      loadReply();
    }
  }, [reviewId]);

  const loadReply = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/reviews/${reviewId}/replies`);
      if (res.ok) {
        const replies = await res.json();
        if (replies.length > 0) {
          setReply(replies[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load reply:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim() || !token) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/social/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          review_id: reviewId,
          content: replyText.trim()
        })
      });

      if (res.ok) {
        const newReply = await res.json();
        setReply(newReply);
        setReplyText('');
        setShowReplyForm(false);
        toast.success('Reply posted successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to post reply');
      }
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm ml-4 mt-3">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Loading reply...</span>
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid={`review-reply-${reviewId}`}>
      {/* Existing Reply */}
      {reply && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="ml-4 pl-4 border-l-2 border-cyan-500/30"
        >
          <div className="bg-cyan-500/5 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Building className="w-3 h-3 text-cyan-400" />
              </div>
              <span className="font-medium text-cyan-400 text-sm">{reply.responder_name}</span>
              <span className="text-xs text-gray-500">• Business Owner</span>
              <span className="text-xs text-gray-500 ml-auto">{formatDate(reply.created_at)}</span>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">{reply.content}</p>
          </div>
        </motion.div>
      )}

      {/* Reply Button (for business owners) */}
      {canReply && !reply && (
        <div className="ml-4 mt-2">
          {!showReplyForm ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReplyForm(true)}
              className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Reply as Business Owner
            </Button>
          ) : (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-3"
              >
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write your response to this review..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 text-sm"
                  rows={3}
                />
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSubmitReply}
                    disabled={!replyText.trim() || submitting}
                    size="sm"
                    className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Posting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Post Reply
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowReplyForm(false)}
                    className="text-gray-400"
                  >
                    Cancel
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      )}
    </div>
  );
}

export default ReviewReplySection;
